(() => {
  'use strict';

  let initialized = false;
  const { scrollLock } = window.MixedownUI;
  const isElement = value => value instanceof Element;

  const initHeader = () => {
    const burgerMenu = document.querySelector('.burger-menu');
    const headerMenu = document.querySelector('.header-menu');
    if (!burgerMenu || !headerMenu) return;

    const overlay = document.querySelector('.header-menu-overlay');
    const closeButton = headerMenu.querySelector('.header-menu__close');
    const mobileQuery = window.matchMedia('(max-width: 749px)');
    const isOpen = () => headerMenu.classList.contains('active');

    const setSubmenuOpen = (item, open) => {
      item.classList.toggle('active', open);
      const link = item.querySelector(':scope > .menu-link');
      if (link) link.setAttribute('aria-expanded', String(open));
    };

    const closeSubmenus = () => {
      headerMenu.querySelectorAll('.menu-item.active').forEach(item => {
        setSubmenuOpen(item, false);
      });
    };

    const setMenuOpen = open => {
      if (open === isOpen()) return;
      headerMenu.classList.toggle('active', open);
      if (overlay) overlay.classList.toggle('active', open);
      burgerMenu.setAttribute('aria-expanded', String(open));
      if (open) {
        headerMenu.setAttribute('role', 'dialog');
        headerMenu.setAttribute('aria-modal', 'true');
        scrollLock.lock();
        if (closeButton) closeButton.focus({ preventScroll: true });
        return;
      }
      headerMenu.removeAttribute('role');
      headerMenu.removeAttribute('aria-modal');
      scrollLock.unlock();
      closeSubmenus();
      if (headerMenu.contains(document.activeElement)) burgerMenu.focus({ preventScroll: true });
    };

    burgerMenu.addEventListener('click', () => setMenuOpen(true));
    if (closeButton) closeButton.addEventListener('click', () => setMenuOpen(false));
    if (overlay) overlay.addEventListener('click', () => setMenuOpen(false));
    mobileQuery.addEventListener('change', () => setMenuOpen(false));

    headerMenu.addEventListener('click', event => {
      if (!isElement(event.target)) return;
      const link = event.target.closest('.menu-link');
      const item = link && link.parentElement;
      if (!item || !item.matches('.menu-item.has-children')) return;

      event.preventDefault();
      const active = headerMenu.querySelector('.menu-item.active');
      if (active && active !== item) setSubmenuOpen(active, false);
      setSubmenuOpen(item, !item.classList.contains('active'));
    });

    document.addEventListener('click', event => {
      if (!isElement(event.target)) return;
      if (event.target.closest('.menu-item')) return;
      closeSubmenus();
    });

    window.addEventListener('keydown', event => {
      if (!isOpen()) return;
      if (event.key === 'Escape') {
        setMenuOpen(false);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(headerMenu.querySelectorAll('a[href], button')).filter(element => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  const whenPageLoaded = callback => {
    const run = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(callback, { timeout: 1500 });
      } else {
        window.setTimeout(callback, 200);
      }
    };
    if (document.readyState === 'complete') {
      run();
    } else {
      window.addEventListener('load', run, { once: true });
    }
  };

  const initVideoBanners = () => {
    const connection = navigator.connection;
    const saveData = Boolean(connection && connection.saveData);

    document.querySelectorAll('.video-banner-container').forEach(container => {
      const video = container.querySelector('.video-banner-media');
      const poster = container.querySelector('.video-banner-poster');
      if (!video || video.dataset.videoInitialized === 'true') return;
      video.dataset.videoInitialized = 'true';

      const lazySources = Array.from(video.querySelectorAll('source[data-src]'));
      if (!lazySources.length || saveData) return;

      let loaded = false;
      let inView = false;

      const showPoster = show => {
        if (poster) poster.classList.toggle('hidden', !show);
      };

      const play = () => {
        const attempt = video.play();
        if (attempt) attempt.catch(() => showPoster(true));
      };

      const load = () => {
        if (loaded) return;
        loaded = true;
        lazySources.forEach(source => {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        video.preload = 'auto';
        video.load();
      };

      video.addEventListener('playing', () => showPoster(false));
      video.addEventListener('error', () => showPoster(true), true);

      const update = () => {
        if (!inView) {
          if (loaded && !video.paused) video.pause();
          return;
        }
        load();
        play();
      };

      whenPageLoaded(() => {
        if (!('IntersectionObserver' in window)) {
          inView = true;
          update();
          return;
        }
        new IntersectionObserver(entries => {
          inView = entries[entries.length - 1].isIntersecting;
          update();
        }, { rootMargin: '300px 0px' }).observe(video);
      });
    });
  };

  const initInfiniteCollections = () => {
    document.querySelectorAll('[data-infinite-collection]').forEach(root => {
      const productGrid = root.querySelector('#product-list');
      const loadingLogo = root.querySelector('#loading-logo');
      const sentinel = root.querySelector('[data-infinite-sentinel]');
      const paginationData = root.querySelector('[data-current-page][data-max-pages]');
      const collectionUrl = root.dataset.collectionUrl;
      if (!productGrid || !loadingLogo || !paginationData || !collectionUrl) return;

      let currentPage = Number.parseInt(paginationData.dataset.currentPage, 10) || 1;
      const maxPages = Number.parseInt(paginationData.dataset.maxPages, 10) || 1;
      let isLoading = false;
      let observer = null;

      const getPageUrl = page => {
        const url = new URL(collectionUrl, window.location.origin);
        url.searchParams.set('page', String(page));
        url.searchParams.set('view', 'ajax');
        return url.toString();
      };

      const loadMoreProducts = () => {
        if (isLoading || currentPage >= maxPages) return;
        isLoading = true;
        loadingLogo.hidden = false;
        currentPage += 1;

        fetch(getPageUrl(currentPage), { credentials: 'same-origin' })
          .then(response => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response;
          })
          .then(response => response.text())
          .then(data => {
            const nextGrid = new DOMParser().parseFromString(data, 'text/html').getElementById('product-list');
            if (nextGrid && nextGrid.children.length > 0) productGrid.append(...Array.from(nextGrid.children));
            if (currentPage >= maxPages && observer) observer.disconnect();
          })
          .catch(() => {
            currentPage -= 1;
          })
          .finally(() => {
            isLoading = false;
            loadingLogo.hidden = true;
          });
      };

      if ('IntersectionObserver' in window && sentinel) {
        observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) loadMoreProducts();
        }, { rootMargin: '500px 0px' });
        observer.observe(sentinel);
      } else {
        let fallbackFrame = null;
        window.addEventListener('scroll', () => {
          if (fallbackFrame) return;
          fallbackFrame = window.requestAnimationFrame(() => {
            fallbackFrame = null;
            if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 500) loadMoreProducts();
          });
        }, { passive: true });
      }
    });
  };

  const initAboutPage = () => {
    const storiesContainer = document.getElementById('storiesContainer');
    if (!storiesContainer) return;

    const stories = Array.from(storiesContainer.children);
    for (let index = stories.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(Math.random() * (index + 1));
      const item = stories[index];
      stories[index] = stories[swapIndex];
      stories[swapIndex] = item;
    }

    const fragment = document.createDocumentFragment();
    stories.forEach(story => fragment.appendChild(story));
    storiesContainer.appendChild(fragment);
  };

  const initCookieBanner = () => {
    let preferencesRequested = false;

    document.addEventListener('click', event => {
      if (!isElement(event.target) || !event.target.closest('[data-cookie-preferences]')) return;
      event.preventDefault();
      preferencesRequested = true;
      document.querySelectorAll('#shopify-pc__prefs').forEach(element => {
        ['display', 'visibility', 'pointer-events'].forEach(property => element.style.removeProperty(property));
      });
      window.privacyBanner?.showPreferences();
    });

    if (document.body.classList.contains('is-privacy-policy-page')) {
      const suppressBanner = () => {
        document.querySelectorAll(preferencesRequested ? '#shopify-pc__banner' : '#shopify-pc__banner, #shopify-pc__prefs').forEach(element => {
          element.style.setProperty('display', 'none', 'important');
          element.style.setProperty('visibility', 'hidden', 'important');
          element.style.setProperty('pointer-events', 'none', 'important');
        });
        document.body.style.removeProperty('overflow');
      };

      suppressBanner();

      const observer = new MutationObserver(suppressBanner);
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style']
      });
      return;
    }

    const bannerTitle = 'Wir verwenden Cookies';

    const updateBanner = () => {
      const title = document.getElementById('shopify-pc__banner__body-title');
      if (!title) return false;
      if (title.textContent !== bannerTitle) title.textContent = bannerTitle;

      const declineButton = document.getElementById('shopify-pc__banner__btn-decline');
      if (!declineButton) return false;

      declineButton.textContent = 'Ablehnen & fortfahren';
      declineButton.classList.add('shopify-pc__banner__btn-decline');

      let heading = title.closest('.shopify-pc__banner__heading');
      if (!heading) {
        heading = document.createElement('div');
        heading.className = 'shopify-pc__banner__heading';
        title.before(heading);
        heading.appendChild(title);
      }

      if (declineButton.parentElement !== heading) heading.appendChild(declineButton);

      return true;
    };

    if (updateBanner()) return;

    const observer = new MutationObserver(() => {
      if (updateBanner()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  const initCustomerTemplates = () => {
    const showRecoverPasswordForm = () => {
      const recover = document.getElementById('recover-password');
      const customer = document.getElementById('customer');
      if (recover) recover.hidden = false;
      if (customer) customer.hidden = true;
    };

    const hideRecoverPasswordForm = () => {
      const recover = document.getElementById('recover-password');
      const customer = document.getElementById('customer');
      if (recover) recover.hidden = true;
      if (customer) customer.hidden = false;
    };

    if (window.location.hash === '#recover') showRecoverPasswordForm();

    document.addEventListener('click', event => {
      if (!isElement(event.target)) return;
      const recoverOpen = event.target.closest('[data-recover-password-open]');
      const recoverClose = event.target.closest('[data-recover-password-close]');
      const addressNewToggle = event.target.closest('[data-address-new-toggle]');
      const addressEdit = event.target.closest('.action_edit a[data-address-id]');

      if (recoverOpen) {
        event.preventDefault();
        showRecoverPasswordForm();
        return;
      }

      if (recoverClose) {
        event.preventDefault();
        hideRecoverPasswordForm();
        return;
      }

      if (addressNewToggle) {
        event.preventDefault();
        const addressForm = document.getElementById('add_address');
        if (addressForm) addressForm.hidden = !addressForm.hidden;
        return;
      }

      if (addressEdit) {
        event.preventDefault();
        const addressId = addressEdit.dataset.addressId;
        const editForm = document.getElementById(`edit_address_${addressId}`);
        const viewAddress = document.getElementById(`view_address_${addressId}`);
        if (!editForm || !viewAddress) return;
        const shouldOpen = editForm.hidden;
        editForm.hidden = !shouldOpen;
        viewAddress.hidden = shouldOpen;
      }
    });

    document.addEventListener('submit', event => {
      const form = event.target.closest('[data-confirm-submit]');
      if (form && !window.confirm(form.dataset.confirmSubmit)) event.preventDefault();
    });
  };

  const init = () => {
    if (initialized) return;
    initialized = true;
    initHeader();
    initVideoBanners();
    initInfiniteCollections();
    initAboutPage();
    initCookieBanner();
    initCustomerTemplates();
  };

  document.addEventListener('shopify:section:load', initVideoBanners);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
